import React from 'react';
import { Link } from 'react-router-dom';
import './ItemDetail.css';

/**
 * Shared component for displaying details of a movie or book
 * 
 * @param {Object} props
 * @param {string} props.type - 'movie' or 'book' 
 * @param {Object} props.item - The item (movie or book) object to display
 * @param {boolean} props.loading - Whether the item is loading
 * @param {string} props.error - Error message, if any
 * @param {boolean} props.retrying - Whether a fetch retry is in progress
 * @param {React.ReactNode} props.imageComponent - Component to render the item's image
 * @param {Object} props.metadata - Key metadata fields to display
 * @param {React.ReactNode} props.extraContent - Additional content to display (e.g., description, links)
 */
function ItemDetail({
  type = 'item',
  item,
  loading,
  error,
  retrying = false,
  imageComponent,
  metadata = {},
  extraContent,
}) {
  const backPath = type === 'movie' ? '/' : '/books';
  const backText = type === 'movie' ? 'Back to Movies' : 'Back to Books';
  
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading">
          Loading {type} details...
          {retrying && <span className="loading-message">(Retrying automatically)</span>}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="item-detail">
        <h2 className="error-message">{error}</h2>
        <Link to={backPath} className="back-button">{backText}</Link>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="item-detail">
        <h2>{type === 'movie' ? 'Movie' : 'Book'} not found</h2>
        <Link to={backPath} className="back-button">{backText}</Link>
      </div>
    );
  }

  return (
    <div className={`item-detail ${type}-detail`}>
      <Link to={backPath} className="back-button">{backText}</Link>
      <div className="item-detail-content">
        {imageComponent}
        <div className="item-info-detailed">
          <h1>{item.title}</h1>
          
          {/* Display metadata fields in the exact order they were defined */}
          {Object.entries(metadata).map(([key, value]) => {
            // Special case for Overview
            const isOverview = key === 'Overview';
            
            return value && (
              <p 
                key={key} 
                className={`metadata-field ${isOverview ? 'overview-field' : ''}`}
              >
                <strong>{key}</strong>
                {isOverview ? 
                  <span>{value}</span> : 
                  <span className="value-container">{value}</span>
                }
              </p>
            );
          })}
          
          {/* Display any extra content */}
          {extraContent}
        </div>
      </div>
    </div>
  );
}

export default ItemDetail; 